import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DateRangePicker } from '@zuruflex/ui';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

interface AvailabilityResult {
  available: boolean;
  reason: string | null;
  nextFreeFrom: string | null;
}

/**
 * Asks the API whether a vehicle is free for a window. Uses the same endpoint
 * the WhatsApp flow calls, so an agent on the phone sees exactly what the bot
 * would tell the customer.
 */
export function AvailabilityCheck({ vehicleId }: { vehicleId: string }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const [range, setRange] = useState<DateRange | undefined>();

  const check = useMutation<AvailabilityResult, Error, { from: Date; to: Date }>({
    mutationFn: ({ from, to }) =>
      api.request<AvailabilityResult>(
        'GET',
        `/v1/vehicles/${vehicleId}/availability?pickupAt=${from.toISOString()}&dropoffAt=${to.toISOString()}`,
      ),
  });

  const ready = Boolean(range?.from && range.to);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('vehicle.availability')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <DateRangePicker
          value={range}
          placeholder={t('vehicle.availabilityRange')}
          onChange={(next) => {
            setRange(next);
            check.reset();
          }}
        />

        <Button
          className="w-full"
          disabled={!ready}
          isLoading={check.isPending}
          onClick={() => {
            if (range?.from && range.to) check.mutate({ from: range.from, to: range.to });
          }}
        >
          {t('common:action.check')}
        </Button>

        {check.error ? <Alert variant="danger">{check.error.message}</Alert> : null}

        {check.data ? (
          check.data.available ? (
            <Alert variant="success" title={t('vehicle.free')}>
              {t('vehicle.freeBody')}
            </Alert>
          ) : (
            <Alert variant="warning" title={t('vehicle.notFree')}>
              {check.data.nextFreeFrom
                ? t('vehicle.nextFree', {
                    when: formatDateTimeIn(check.data.nextFreeFrom, locale),
                  })
                : t('vehicle.blocked', {
                    reason: check.data.reason
                      ? t(`common:vehicleStatus.${check.data.reason}`, {
                          defaultValue: check.data.reason.toLowerCase(),
                        })
                      : '',
                  })}
            </Alert>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}
